import { Carousel } from '@eventbrite/eds-carousel';
import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import { gettext } from '@eventbrite/i18n';
import React, { useContext, useRef } from 'react';
import { TrackingContext } from '../../context';
import '../../styles/related-events.scss';
import {
    label,
    shareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import { RelatedEventsChildrenProps } from './RelatedEvents';

const CAROUSEL_HEADING = gettext('Other events you may like');

const getEventCardMap = ({
    events,
    promotedEvents,
    isAuthenticated,
    shareComponentProps,
    enable_fe_placement_on_related_events,
}: {
    events: FormattedEvent[] | undefined;
    promotedEvents: FormattedEvent[] | undefined;
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    enable_fe_placement_on_related_events: boolean;
}): JSX.Element[] => {
    if (!events || !promotedEvents) {
        return [];
    }

    const eventsToRender = [...events];

    if (enable_fe_placement_on_related_events) {
        eventsToRender.unshift(...promotedEvents);
    }

    return eventsToRender.map((event: FormattedEvent) => {
        const shareEventProps = {
            eventId: event.id,
            eventName: event.name,
            eventUrl: event.url,
            ...shareComponentProps,
        };

        const eventKey = event.promotedListingMetadata?.ad_id || event.id;

        return (
            <EventCard
                {...CONSUMER_CARD_DEFAULTS}
                affCode={AffiliateCodes.AFF_RELATED_EVENTS_LIVE}
                key={eventKey}
                eventData={event}
                gaCategory="related-events"
                isAuthenticated={isAuthenticated}
                shareOptions={shareEventProps}
                onDidShareClick={(id: shareType) =>
                    trackShareAction(label(id), GACodes.GA_CATEGORY_EVENT_CARD)
                }
                onWillShareClick={() =>
                    trackShareAction(
                        GACodes.GA_LABEL_OPEN_MODAL,
                        GACodes.GA_CATEGORY_EVENT_CARD,
                    )
                }
            />
        );
    });
};

function getEventFromId(props: RelatedEventsChildrenProps, id: string) {
    const events = props.events || [];
    const event = events.find((e) => e.id === id);
    const promotedEvents = props.promotedEvents || [];
    const promotedEvent = promotedEvents.find((e) => e.id === id);
    if (promotedEvent) {
        return { event: promotedEvent, events: promotedEvents };
    }
    return { event, events };
}

const DESKTOP_SLIDE_SIZE = 3;
const MOBILE_SLIDE_SIZE = 1;

const OtherEventsYouMayLike = (props: RelatedEventsChildrenProps) => {
    const domElement = useRef<HTMLElement>(null);
    const currentPage = useRef({ number: 0, size: 0 });
    const context = useContext(TrackingContext);

    usePlacementEventContextAggregator(
        domElement,
        ({ id, action }) =>
            aggregateContext({
                id,
                action,
                context,
                page: currentPage.current,
                props,
            }),
        context.featureFlags?.enable_fe_tracking_on_related_events,
    );

    const eventCardMap = getEventCardMap({
        events: props.events,
        promotedEvents: props.promotedEvents || [],
        isAuthenticated: props.isAuthenticated,
        shareComponentProps: props.shareComponentProps,
        enable_fe_placement_on_related_events:
            context.featureFlags?.enable_fe_placement_on_related_events ||
            false,
    });

    const wrapEventCardMapForDesktop = () => {
        const slides = [];

        for (let i = 0; i < eventCardMap.length; i += DESKTOP_SLIDE_SIZE) {
            const slideItem = eventCardMap.filter(
                (_, index) => index >= i && index < i + DESKTOP_SLIDE_SIZE,
            );
            slides.push(slideItem);
        }
        return slides;
    };

    const eventCardMapForDesktop = wrapEventCardMapForDesktop().map(
        (slide, index) => {
            return (
                <div className="desktop-slide" key={index}>
                    {slide}
                </div>
            );
        },
    );
    if (!props.events || props.events.length <= 0) {
        return null;
    }
    return (
        <section
            className="related-events g-grid g-grid--page-margin-manual"
            ref={domElement}
        >
            <div className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 eds-l-pad-hor-5 eds-l-ln-pad-hor-0 eds-l-lg-pad-hor-0 eds-l-lw-pad-hor-0 eds-l-mar-bot-16">
                <div className="eds-show-up-mn" data-testid="carousel-desktop">
                    <Carousel
                        onTransition={(toPage: number) => {
                            currentPage.current = {
                                number: toPage,
                                size: DESKTOP_SLIDE_SIZE,
                            };
                        }}
                        hasTopControls={true}
                        isNotContinuous={true}
                        alwaysShowSideNavigation={true}
                        usesNewStyle={true}
                        title={CAROUSEL_HEADING}
                        fullWidth={true}
                    >
                        {eventCardMapForDesktop}
                    </Carousel>
                </div>

                <div
                    className="eds-show-down-sw carousel-mobile"
                    data-testid="carousel-mobile"
                >
                    <Carousel
                        onTransition={(toPage: number) => {
                            currentPage.current = {
                                number: toPage,
                                size: MOBILE_SLIDE_SIZE,
                            };
                        }}
                        alwaysShowSideNavigation={true}
                        hasTopControls={true}
                        isNotContinuous={true}
                        usesNewStyle={true}
                        title={CAROUSEL_HEADING}
                        fullWidth={true}
                    >
                        {eventCardMap}
                    </Carousel>
                </div>
            </div>
        </section>
    );
};

const RELATED_EVENTS_PLACEMENT_NAME = 'related_events';
const LIVE_EVENTS_PLACEMENT_NAME = 'events_carousel';

function aggregateContext({ action, id, context, page, props }: any) {
    const { event, events } = getEventFromId(props, id);
    return {
        user: {
            id: context.userId,
            locale: context.locale,
        },
        item: { id: id },
        adId: event?.promotedListingMetadata?.ad_id,
        action: action,
        place: {
            name: RELATED_EVENTS_PLACEMENT_NAME,
            position:
                events.findIndex((event) => event?.id === id) +
                1 -
                page.number * page.size,
            page: page.number + 1,
            context: {
                related_event_id: context.event?.id,
                is_online: context.event?.isOnline,
            },
            tabKey: '',
            subInterface: {
                name: LIVE_EVENTS_PLACEMENT_NAME,
                position: '',
            },
        },
    };
}

export default OtherEventsYouMayLike;
